import { colorAttentionBlue10, colorFunctionalGray70, colorFunctionalWhite, colorMiscGray90, fontSizeS, iconSizeM, spacingS, spacingXs, spacingXxs, } from "@rhumbix/rmbx_design_system_design_tokens";
import React, { useEffect, useState } from "react";
import Select, { components } from "react-select";
import styled from "styled-components";
import { selectionButtonStyles } from "../../constants";
import { getBoldFixStyles } from "../../utils";
import Copy from "../Copy";
// Override/extend react-select's default components
const customComponents = {
    Control: ({ children, ...props }) => {
        const { iconColor, renderIcon, setActive, setHovered, setPressed } = props.selectProps;
        useEffect(() => setActive(props.menuIsOpen), [props.menuIsOpen]);
        return (React.createElement(ControlContainer, { onMouseOver: () => setHovered(true), onMouseOut: () => {
                setHovered(false);
                setPressed(false);
            }, onMouseDown: () => setPressed(true), onMouseUp: () => setPressed(false) },
            React.createElement(components.Control, { ...props },
                renderIcon && React.createElement(ButtonIcon, null, renderIcon(iconColor)),
                children)));
    },
    NoOptionsMessage: props => {
        const { noOptionsComponent } = props.selectProps;
        return noOptionsComponent ? noOptionsComponent() : React.createElement(components.NoOptionsMessage, { ...props });
    },
    DropdownIndicator: ({ selectProps }) => (React.createElement(DropdownChevron, { color: selectProps.iconColor },
        React.createElement("div", { className: "downArrow" }))),
    IndicatorSeparator: () => React.createElement(React.Fragment, null),
    Menu: props => {
        const { dropdownSubtitle } = props.selectProps;
        return (React.createElement(components.Menu, { ...props },
            React.createElement(React.Fragment, null,
                dropdownSubtitle && React.createElement(DropdownSubtitleDiv, null, dropdownSubtitle),
                props.children)));
    },
    Option: props => {
        const { renderOption } = props.selectProps;
        return (React.createElement(components.Option, { ...props }, renderOption ? (renderOption(props.data, props.label)) : (React.createElement(Copy, { color: colorMiscGray90 }, props.label))));
    },
};
const ActionBarSelector = ({ renderIcon, renderOption, menuStyles, onlyDisplayIcon, dropdownSubtitle, ...props }) => {
    const [active, setActive] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);
    const { bgColor, fontWeight, iconColor, textColor } = pressed
        ? selectionButtonStyles.pressed
        : hovered
            ? selectionButtonStyles.hovered
            : active
                ? selectionButtonStyles.active
                : selectionButtonStyles.default;
    const customStyles = {
        clearIndicator: base => ({
            ...base,
            color: iconColor,
            svg: {
                height: iconSizeM,
                width: iconSizeM,
            },
            ":hover": {
                color: iconColor,
                cursor: "pointer",
            },
        }),
        control: base => ({
            ...base,
            backgroundColor: bgColor,
            border: 0,
            boxShadow: "none",
            width: "fit-content",
            ":hover": {
                cursor: "pointer",
            },
        }),
        menu: base => ({
            ...base,
            minWidth: "max-content",
            ...menuStyles,
        }),
        option: (base, state) => ({
            ...base,
            ...(renderOption
                ? {
                    padding: 0,
                }
                : null),
            backgroundColor: state.isSelected
                ? selectionButtonStyles.active.bgColor
                : selectionButtonStyles.default.bgColor,
            ":hover": {
                cursor: "pointer",
                backgroundColor: colorAttentionBlue10,
            },
        }),
        placeholder: () => ({
            fontSize: fontSizeS,
            fontWeight,
            color: textColor,
            "::after": {
                ...getBoldFixStyles(`${props.placeholder ?? ""}`),
            },
        }),
        singleValue: (_, state) => ({
            fontSize: fontSizeS,
            fontWeight,
            color: textColor,
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            contentVisibility: onlyDisplayIcon ? "hidden" : "auto",
            "::after": {
                ...getBoldFixStyles(state.data.label),
            },
        }),
        valueContainer: base => ({
            ...base,
            padding: `${spacingXxs} 0 ${spacingXxs} ${spacingS}`,
        }),
    };
    return (React.createElement(Select, { ...props, components: customComponents, styles: customStyles, isSearchable: false, 
        // The following are custom props passed to react-select,
        // and are forwarded to custom components via the selectProps prop:
        iconColor: iconColor, renderIcon: renderIcon, renderOption: renderOption, setActive: setActive, setHovered: setHovered, setPressed: setPressed, dropdownSubtitle: dropdownSubtitle }));
};
const DropdownChevron = styled.div `
    padding: ${spacingS};

    :hover {
        cursor: pointer;
    }

    .downArrow {
        width: 0;
        height: 0;
        border-left: ${spacingXs} solid transparent;
        border-right: ${spacingXs} solid transparent;
        border-top: 5px solid ${props => props.color};
    }
`;
const DropdownSubtitleDiv = styled.div `
    display: flex;
    height: 30px;
    background-color: ${colorFunctionalGray70};
    font-size: 16px;
    padding-left: 31px;
    align-items: center;
    font-weight: 700;
    color: ${colorFunctionalWhite};
    margin-bottom: -4px;
`;
const ButtonIcon = styled.div `
    margin-left: ${spacingS};
    display: flex;
    align-items: center;
    justify-content: center;
`;
const ControlContainer = styled.div `
    width: fit-content;
`;
export default ActionBarSelector;
